import { ReactElement, useContext, useEffect } from 'react';

import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import Head from 'next/head';

import { Container } from '@/components/Atoms/Container/Container';
import { SecondNav } from '@/components/Molecules/SecondNav/SecondNav';
import { PostList } from '@/components/Organisms/PostList/PostList';
import { AdsContext } from '@/context/ads/AdsProvider';
import { useBackToArticle } from '@/hooks/useBackToArticle';
import useGTMPageView from '@/hooks/useGTMPageView';
import { Main } from '@/templates/Main';
import { isProduction } from '@/utils/getEnviroment';
import { secondsIn } from '@/utils/Time/Time';
import { addApolloState, getTag, usePosts } from '@wordpress';
import { IAdKeyValues } from 'types/Ads';

const TagPage = ({
	tag,
	cursor,
	hasMorePosts,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
	useBackToArticle();

	useGTMPageView({
		pageType: 'tag',
		author: '',
		category: '',
		vertical: '',
	});

	const { setTargets } = useContext(AdsContext);

	const { getPosts, isLoading, hasMore, posts } = usePosts({
		posts: tag.posts,
		tag: tag.slug,
		cursor,
		hasMorePosts,
	});

	useEffect(() => {
		const targets = {
			section: tag.slug,
			pageType: 'tag',
			staging: !isProduction,
		} as IAdKeyValues;

		setTargets(targets);
	}, [setTargets, tag.slug]);

	const seoProps: NextSeoProps = {
		titleTemplate: '%s',
		title: tag.seo.title || tag.name || '',
		canonical: tag.seo.canonical || '',
		description: tag.seo.description || '',

		openGraph: {
			url: tag.seo.openGraph.url || '',
			description: tag.seo.openGraph.description || '',
		},
	};

	return (
		<div className="flex flex-col pt-8">
			<NextSeo {...seoProps} />
			{tag.seo.schema && (
				<Head>
					<script
						id="tag-jsonld"
						type="application/ld+json"
						dangerouslySetInnerHTML={{ __html: tag.seo.schema }}
					/>
				</Head>
			)}
			<Container>
				<div className="col-span-full">
					<SecondNav className="mx-4 mb-5 md:mb-10 lg:mx-0" title={tag.name} />
					<p className="font-body text-body-3 font-medium text-neutral-grey md:mb-5">
						{tag.seo.description}
					</p>
					{isLoading && <p>Loading...</p>}
					{!isLoading && posts?.length === 0 && <p>No posts found.</p>}
				</div>
			</Container>
			<PostList
				level="h2"
				isSidebarDisplayed
				className="px-4 lg:px-0"
				posts={posts}
				hasMorePosts={hasMore}
				loadMore={getPosts}
				// category={slug}
			/>
		</div>
	);
};

TagPage.getLayout = function getLayout(page: ReactElement) {
	return (
		<Main
			pageType="tag"
			mainMenuItems={page.props.mainMenuItems as NavigationItem[] | null}
			footerItems={page.props.footerItems as FooterItem[] | null}
		>
			{page}
		</Main>
	);
};

export function getStaticPaths() {
	return {
		paths: [],
		fallback: 'blocking',
	};
}

export const getStaticProps: GetStaticProps<{
	tag: Tag;
	hasMorePosts: boolean;
	cursor: string;
}> = async (context) => {
	const id: string = context.params?.slug as string;

	const data = await getTag({ id });

	if (!data)
		return {
			notFound: true,
			revalidate: secondsIn.minute,
		};

	const tagProps = {
		tag: data?.tag,
		cursor: data?.cursor,
		hasMorePosts: data?.hasMorePosts,
	};

	return {
		props: addApolloState(data.client, tagProps),
		revalidate: secondsIn.minute,
	};
};

export default TagPage;
